import { type FormDocument, type FormField, type FormPage } from "@/feature/ewbci";

// mmをptに変換
// 1インチ = 25.4mm
// 1インチ = 72pt
// よって、1mm = 72 / 25.4 pt
export const mmToPt = (mm: number) => Number(((mm / 25.4) * 72).toFixed(6));

// mmをpxに変換
// 1インチ = 25.4mm
// 1インチ = 96px
// よって、1mm = 96 / 25.4 px
export const mmToPx = (mm: number) => mm * (96 / 25.4);

export const errmsg = (e: unknown) => {
    let errorMessage: string;
    if (e instanceof Error) {
        // Errorオブジェクトの場合
        errorMessage = `${e.name}${e.message}\n${e.stack}${e.cause && e !== e.cause ? `\ncause: ${errmsg(e.cause)}` : ''}`;
    } else if (typeof e === 'object' && e !== null) {
        // オブジェクトの場合
        try {
            // JSON形式に変換
            errorMessage = JSON.stringify(e, null, 2);
        } catch (stringifyError) {
            // JSON.stringifyが失敗した場合（循環参照など）
            errorMessage = `[Object with circular reference]: ${String(e)} `;
        }
    } else {
        // その他のプリミティブ型
        errorMessage = String(e);
    }
    return errorMessage;
}

export function stableStringify(obj: any): string {
    if (Array.isArray(obj)) {
        return "[" + obj.map(stableStringify).join(",") + "]";
    } else if (obj && typeof obj === "object") {
        return "{" + Object.keys(obj).sort()
            .map(k => JSON.stringify(k) + ":" + stableStringify(obj[k]))
            .join(",") + "}";
    } else {
        return JSON.stringify(obj);
    }
}

/**
 * JSONオブジェクトのSHA-256ハッシュを高速生成
 * パフォーマンス最優先の実装
 */
export async function hashJson(obj: any): Promise<string> {
    // SHA-256ハッシュを生成（最適化済み）
    const data = new TextEncoder().encode(stableStringify(obj));
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);

    // 高速な16進変換
    const view = new DataView(hashBuffer);
    let result = '';
    for (let i = 0; i < view.byteLength; i += 4) {
        result += view.getUint32(i).toString(16).padStart(8, '0');
    }
    return result;
}

export const compkey = (a: any, b: any) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0);

export function equealsField(a: FormField | undefined, b: FormField | undefined): boolean {
    if (a === b) return true;
    if (!a || !b) return false;
    return !(a.key !== b.key || a.rect.x !== b.rect.x || a.rect.y !== b.rect.y || a.rect.w !== b.rect.w || a.rect.h !== b.rect.h || a.readonly !== b.readonly);
}
export function equalsPage(a: FormPage | undefined, b: FormPage | undefined): boolean {
    if (a === b) return true;
    if (!a || !b) return false;
    if (a.id !== b.id
        || a.index !== b.index
        || a.name !== b.name
        || a.size.w !== b.size.w || a.size.h !== b.size.h
        || a.background !== b.background) return false;
    if (a.fields === b.fields) return true;
    if (!a.fields || !b.fields || a.fields.length !== b.fields.length) return false;
    const fa = a.fields.sort(compkey);
    const fb = b.fields.sort(compkey);
    for (let i = 0; i < fa.length; i++) {
        if (!equealsField(fa[i], fb[i])) return false;
    }
    return true;
}
export function equalsDoc(a: FormDocument | null, b: FormDocument | null): boolean {
    if (a === b) return true;
    if (!a || !b || a.id !== b.id || a.name !== b.name || a.pages.length !== b.pages.length) return false;
    // 念の為ソート
    const pa = a.pages.sort((x, y) => x.index - y.index);
    const pb = b.pages.sort((x, y) => x.index - y.index);
    for (let i = 0; i < pa.length; i++) {
        if (!equalsPage(pa[i], pb[i])) return false;
    }
    return true;
}
